// Web-Push subscribe-kern, gedeeld door de /me-opt-in-kaart (entrypoints/me.js)
// en de topbar-notificatiebel (custom/notif_bell.js). Registreert het toestel
// bij de server (POST /me/push_subscription) en is zelfherstellend na
// VAPID-sleutelrotatie: een bestaande browser-subscription onder een ándere
// applicationServerKey wordt eerst opgezegd (anders weigert de browser met
// InvalidStateError) en daarna opnieuw aangemaakt.

export function pushSupported () {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Persistente per-user opt-out ("Turn off push" in de bel): zonder deze vlag
// zouden de stille her-registratiepaden (bel + /me) een bewust uitgezette
// push in de volgende sessie gewoon weer aanzetten. Expliciet aanzetten wist
// de vlag. localStorage (niet sessionStorage): moet tabs en sessies overleven.
function pushOptKey (userId) {
  return 'aimPushOff:' + (userId || '');
}

export function isPushOptedOut (userId) {
  try { return window.localStorage.getItem(pushOptKey(userId)) === '1'; } catch (e) { return false; }
}

export function setPushOptOut (userId, off) {
  try {
    if (off) window.localStorage.setItem(pushOptKey(userId), '1');
    else window.localStorage.removeItem(pushOptKey(userId));
  } catch (e) { /* private mode zonder storage — dan geen persistentie */ }
}

// VAPID public key (base64url) -> Uint8Array voor pushManager.subscribe.
function vapidBytes (base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// True als de bestaande subscription met dezelfde VAPID-key is aangemaakt als
// waarmee we nu zouden subscriben.
function sameServerKey (sub, wanted) {
  const cur = sub.options && sub.options.applicationServerKey;
  if (!cur) return true; // niet vergelijkbaar — neem ongewijzigd aan
  const a = new Uint8Array(cur);
  if (a.length !== wanted.length) return false;
  for (let i = 0; i < a.length; i++) { if (a[i] !== wanted[i]) return false; }
  return true;
}

// Subscribe (of hergebruik) en registreer bij de server. Resolvet met de
// fetch-Response (caller checkt r.ok); rejects bij denied/timeout/receiver —
// caller vangt zelf af.
export function ensurePushSubscription (vapidKey) {
  const wanted = vapidBytes(vapidKey);
  // .ready settelt nooit wanneer de SW-registratie faalde (lib/pwa.js) — time
  // out zodat de catch draait i.p.v. eeuwig hangen.
  const ready = Promise.race([
    navigator.serviceWorker.ready,
    new Promise(function (_, reject) { setTimeout(reject, 10000, new Error('sw never ready')); })
  ]);
  return ready
    .then(function (reg) {
      return reg.pushManager.getSubscription().then(function (existing) {
        if (existing && sameServerKey(existing, wanted)) return existing;
        const drop = existing ? existing.unsubscribe().catch(function () {}) : Promise.resolve();
        return drop.then(function () {
          return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: wanted });
        });
      });
    })
    .then(function (sub) {
      const keys = (sub.toJSON() || {}).keys || {};
      const token = (document.querySelector('meta[name="csrf-token"]') || {}).content;
      return fetch('/me/push_subscription', {
        method: 'POST',
        headers: { 'X-CSRF-Token': token, 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ endpoint: sub.endpoint, p256dh: keys.p256dh, auth: keys.auth })
      });
    });
}
